import { TransformationSuggestion } from '../../api/types.js';
import { getDefaultImageUrl } from '../imageUtils.js';

/**
 * Modal dialog for "suggested transformations" — the dual of
 * IngredientRecipeSelector: instead of picking a subgraph to *decompose*
 * an ingredient into, this picks a ready-made (ingredient, transformation)
 * pair (or a bare transformation) to use as a new shared parent for the
 * current selection. Always offers an explicit "blank node" option,
 * matching today's default Transform behavior.
 */
export class TransformationSuggestionSelector {
  private suggestions: TransformationSuggestion[];

  constructor(suggestions: TransformationSuggestion[]) {
    this.suggestions = suggestions;
  }

  /**
   * Open the modal. Resolves with the picked suggestion, 'blank' for the
   * explicit empty-node option, or null if dismissed (Escape / × / click
   * outside / Cancel·la) — dismissing creates nothing, same as cancelling
   * "Afegeix elaboració" today.
   */
  async open(): Promise<TransformationSuggestion | 'blank' | null> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className =
        'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm';
      overlay.style.animation = 'fadeIn 150ms ease-out';

      const dialog = document.createElement('div');
      dialog.className =
        'bg-[#1a1a2e] border border-[var(--accent-alpha-27)] rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden';
      dialog.style.animation = 'slideUp 200ms ease-out';

      // ── Header ──
      const header = document.createElement('div');
      header.className =
        'flex items-center justify-between px-6 py-4 border-b border-[var(--accent-alpha-20)]';

      const title = document.createElement('h2');
      title.className = 'text-lg font-bold text-[var(--accent)] font-mono';
      title.textContent = 'Transformacions suggerides';
      header.appendChild(title);

      const closeBtn = document.createElement('button');
      closeBtn.className =
        'text-[#888] hover:text-[#e0e0e0] text-2xl leading-none px-2 transition-colors';
      closeBtn.textContent = '×';
      closeBtn.title = 'Tanca';
      closeBtn.addEventListener('click', () => {
        this.closeOverlay();
        resolve(null);
      });
      header.appendChild(closeBtn);

      dialog.appendChild(header);

      // ── Body (scrollable list) ──
      const body = document.createElement('div');
      body.className = 'flex-1 overflow-y-auto p-4 space-y-3';

      body.appendChild(this.createBlankCard(resolve));

      if (this.suggestions.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'text-center py-6 text-[#888] font-mono text-sm';
        empty.textContent = 'No s\'ha trobat cap suggeriment';
        body.appendChild(empty);
      } else {
        for (const suggestion of this.suggestions) {
          body.appendChild(this.createSuggestionCard(suggestion, resolve));
        }
      }

      dialog.appendChild(body);

      // ── Footer ──
      const footer = document.createElement('div');
      footer.className =
        'flex justify-end px-6 py-3 border-t border-[var(--accent-alpha-20)]';

      const cancelBtn = document.createElement('button');
      cancelBtn.className =
        'px-4 py-2 text-sm font-mono text-[#a0a0a0] hover:text-[#e0e0e0] transition-colors rounded-md hover:bg-[var(--accent-alpha-13)]';
      cancelBtn.textContent = 'Cancel·la';
      cancelBtn.addEventListener('click', () => {
        this.closeOverlay();
        resolve(null);
      });
      footer.appendChild(cancelBtn);

      dialog.appendChild(footer);

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeOverlay();
          resolve(null);
        }
      });

      const keyHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          this.closeOverlay();
          resolve(null);
          document.removeEventListener('keydown', keyHandler);
        }
      };
      document.addEventListener('keydown', keyHandler);

      dialog.focus();
    });
  }

  private createBlankCard(
    resolve: (value: TransformationSuggestion | 'blank' | null) => void,
  ): HTMLElement {
    const card = document.createElement('div');
    card.className =
      'border border-dashed border-[var(--accent-alpha-27)] rounded-lg p-4 cursor-pointer ' +
      'hover:border-[var(--accent)] hover:bg-[var(--accent-alpha-7)] transition-colors ' +
      'text-center text-[#a0a0a0] font-mono text-sm';
    card.textContent = 'Cap (node buit)';
    card.addEventListener('click', () => {
      this.closeOverlay();
      resolve('blank');
    });
    return card;
  }

  private createSuggestionCard(
    suggestion: TransformationSuggestion,
    resolve: (value: TransformationSuggestion | 'blank' | null) => void,
  ): HTMLElement {
    const card = document.createElement('div');
    card.className =
      'border border-[var(--accent-alpha-20)] rounded-lg p-4 cursor-pointer ' +
      'hover:border-[var(--accent)] hover:bg-[var(--accent-alpha-7)] transition-colors flex items-center gap-3';

    const imageUrl = suggestion.ingredient ? getDefaultImageUrl(suggestion.ingredient.images) : undefined;
    const imageWrap = document.createElement('div');
    imageWrap.className =
      'w-12 h-12 rounded-full flex-shrink-0 bg-[#0d1117] border border-[var(--accent-alpha-20)] ' +
      'overflow-hidden flex items-center justify-center text-[#555] text-lg';
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = '';
      img.className = 'w-full h-full object-cover';
      img.onerror = () => { img.style.display = 'none'; };
      imageWrap.appendChild(img);
    } else {
      imageWrap.textContent = '?';
    }
    card.appendChild(imageWrap);

    const info = document.createElement('div');
    info.className = 'flex-1 min-w-0';

    const titleEl = document.createElement('div');
    titleEl.className = 'text-sm font-bold text-[#e0e0e0] font-mono truncate';
    titleEl.textContent = suggestion.ingredient?.name ?? `[${suggestion.transformation.name}]`;
    info.appendChild(titleEl);

    if (suggestion.ingredient) {
      const trafoChip = document.createElement('div');
      trafoChip.className =
        'text-xs mt-1 inline-block px-2 py-0.5 rounded-full bg-[#1e1e2e] border border-[#333] text-[#888] font-mono';
      trafoChip.textContent = suggestion.transformation.name;
      info.appendChild(trafoChip);
    }

    card.appendChild(info);

    const badge = document.createElement('span');
    badge.className =
      'text-xs font-mono px-2 py-0.5 rounded-full bg-[var(--accent-alpha-13)] text-[var(--accent)] flex-shrink-0';
    badge.title = suggestion.score != null
      ? 'Similitud amb la selecció'
      : 'Nombre de receptes on s\'ha vist aquesta transformació';
    badge.textContent = suggestion.score != null
      ? `${Math.round(suggestion.score * 100)}%`
      : `${suggestion.count}×`;
    card.appendChild(badge);

    card.addEventListener('click', () => {
      this.closeOverlay();
      resolve(suggestion);
    });

    return card;
  }

  private closeOverlay(): void {
    const els = document.querySelectorAll('.fixed.inset-0.z-50');
    els.forEach((el) => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }
}
